import { WabunkaPaymentMethod } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_payment_method';
import {
  type WabunkaReservation,
  type WabunkaReservationExperienceEventOptionInputRequest,
  type WabunkaReservationGuest,
  type WabunkaReservationInputRequest,
  type WabunkaReservationQuestionAnswerInputRequest,
  WabunkaReservedExperienceEventInterpreterOption,
  WabunkaReservedExperienceEventOption,
} from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_reservation';
import dayjs from 'dayjs';
import cloneDeep from 'lodash/cloneDeep';

import { createDefaultEnExperienceViewModel, type EnExperienceViewModel } from '@/models/enExperience';
import { createDefaultWabunkaExperienceEventInterpreterOptionViewModel, type WabunkaExperienceEventInterpreterOptionViewModel } from '@/models/wabunka/wabunkaExperienceEventInterpreterOption';
import { type WabunkaExperienceEventOptionViewModel } from '@/models/wabunka/wabunkaExperienceEventOption';
import type { WabunkaExperienceOptionViewModel } from '@/models/wabunka/wabunkaExperienceOption';
import {
  buildWabunkaReservationQuestionAnswerListViewModel,
  type WabunkaReservationQuestionAnswerViewModel,
  type WabunkaReservationQuestionViewModel,
} from '@/models/wabunka/wabunkaReservationQuestionAnswer';
import { toJstCalendarDate } from '@/utils/date';
import type { PaymentMethod } from '@/wabunka-src/constants/paymentMethod';
import type { WabunkaCode } from '@/wabunka-src/features/Experience';
import {
  createDefaultWabunkaExperienceEventViewModel,
  type WabunkaExperienceEventViewModel,
  buildWabunkaReservationExperienceEventViewModel,
  createDefaultWabunkaReservationExperienceEventViewModel,
  type WabunkaReservationExperienceEventViewModel,
} from '@/wabunka-src/features/Experience/models/experienceEvent';

import type {
  WabunkaExperiencePreReservationGuestTypeBasedPriceCalculationRequestInput,
  WabunkaExperienceGuestTypePriceCalculationRequestInput,
} from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_experience_guest_type_based_price';

/**
 * 予約時のオプション選択フォーム
 */
export interface WabunkaReservationExperienceEventOptionForm {
  experienceEventOption: WabunkaExperienceEventOptionViewModel | WabunkaExperienceOptionViewModel;
  count: number;
}

export interface WabunkaReservationFormStep1 {
  /** 予約人数 */
  numOfParticipants: number;
  /** 選択された日付 バリデーション用 */
  selectedDate: dayjs.Dayjs | null;
  /** 体験イベントID */
  experienceEventId: string;
  /** 合計税込金額(GAイベント送信のために計算結果を保持する用) */
  totalPriceIncludingTax: number;
  /** バリデーションを通ったかどうか */
  isValidated: boolean;
  /** ゲストタイプ別人数 */
  guestCounts?: Record<string, number>;
}

/**
 * 予約導線中でリロードされても復元できるように、体験詳細画面での入力データとその先の予約導線での画面表示に使うデータを保存する。
 * NOTE: ローカルストレージを利用したカート機能実装時にはこのモデルを必要に応じて修正しカートの中身として利用する。
 */
export interface WabunkaReservationCartItem {
  formStep1: WabunkaReservationFormStep1;
  experienceEvent: WabunkaExperienceEventViewModel;
  /** formStep2のreservationQuestionAnswersの内answerを除いたプロパティのみ */
  reservationQuestions: WabunkaReservationQuestionViewModel[];
  /** カートに入れた日時 */
  cartAddedAt: dayjs.Dayjs;
  /** MicroCMSから取得したID（slug） */
  cmsId: string;
  /** MicroCMSから取得したタイトル */
  title: string;
  /** MicroCMSから取得したPCメイン画像URL */
  pcMainImageUrl: string;
}

export interface WabunkaReservationCart {
  version: number;
  items: WabunkaReservationCartItem[];
}

export interface WabunkaReservationFormStep2 {
  /** 名 */
  firstName: string;
  /** 姓 */
  lastName: string;
  /** メールアドレス */
  email: string;
  /** 会社名 */
  companyName: string;
  /** For myself / For someone else 切り替え (false = For myself, true = For someone else) */
  shouldShowSomeoneElseForm: boolean;
  /** For myself: 電話番号 */
  forMyselfPhoneNumber: string;
  /** For myself: 国籍 */
  forMyselfNationality: string;
  /** For someone else: 参加代表者名 */
  participantFirstName: string;
  /** For someone else: 参加代表者姓 */
  participantLastName: string;
  /** For someone else: 電話番号 */
  forSomeoneElsePhoneNumber: string;
  /** For someone else: 国籍 */
  forSomeoneElseNationality: string;
  /** For someone else: メールアドレス */
  forSomeoneElseEmail: string;
  /** 予約する体験イベントオプション配列 */
  experienceEventOptions: WabunkaReservationExperienceEventOptionForm[];
  /** 予約する体験イベント通訳オプション */
  experienceEventInterpreterOption: WabunkaExperienceEventInterpreterOptionViewModel;
  /** 予約時質問回答 */
  reservationQuestionAnswers: WabunkaReservationQuestionAnswerViewModel[];
  /**
   * 3DS 失敗後の restore API レスポンスから一時保存した質問回答
   * setupFormWithQueryParams 呼び出し時に reservationQuestionAnswers へ反映・クリアされる
   */
  _pendingRestoreAnswers?: { questionId: string; answer: string }[];
  /**
   * 3DS 失敗後の restore API レスポンスから一時保存した体験イベントオプション選択
   * setupFormWithQueryParams 呼び出し時に experienceEventOptions へ反映・クリアされる
   */
  _pendingRestoreOptions?: { experienceEventOptionId: string; count: number }[];
  /** バリデーションを通ったかどうか */
  isValidated: boolean;
  /** クレジットカードトークン */
  cardToken: string;
  /** クレジットカード最終4桁 */
  cardLastFourDigits: string;
  /** 決済でエラーになり、カード再入力が済むまで確定させない状態か。表示文言とは独立に持つ（文言が空でも導線を止めるため） */
  paymentFailed: boolean;
  /** コード */
  code: WabunkaCode;
}

/**
 * Wabunka 予約内容ベースモデル
 * ビューでは使われないが、GAイベントなど、内部で使われるプロパティをここに定義する
 */
export type WabunkaReservationBase = Readonly<{
  id: string;
  caseId: string;
  guest: WabunkaReservationGuest | undefined;
  totalTax: number;
  totalPriceIncludingTax: number;
  reservedAt: string;
  /** 予約体験イベント */
  reservationExperienceEvent: WabunkaReservationExperienceEventViewModel;
  /** 体験イベントオプション */
  experienceEventOptions: WabunkaReservedExperienceEventOption[];
  /** 体験イベント通訳オプション */
  experienceEventInterpreterOption?: WabunkaReservedExperienceEventInterpreterOption | undefined;
}>;

export type WabunkaReservationWithGuestTypeBase = Omit<WabunkaReservationBase, 'guest'> & {
  guestTypes: WabunkaExperienceGuestTypePriceCalculationRequestInput[];
};

const createDefaultWabunkaReservationBase = (): WabunkaReservationBase => ({
  id: '',
  caseId: '',
  guest: {
    email: '',
    firstName: '',
    lastName: '',
    companyName: '',
    participantFirstName: '',
    participantLastName: '',
    participantPhoneNumber: '',
    participantNationality: '',
    participantEmail: '',
  },
  totalTax: 0,
  totalPriceIncludingTax: 0,
  reservedAt: '',
  reservationExperienceEvent: createDefaultWabunkaReservationExperienceEventViewModel(),
  experienceEventOptions: [],
  experienceEventInterpreterOption: undefined,
});

export interface WabunkaReservationForm {
  experienceFromCMS: EnExperienceViewModel;
  experienceEvent: WabunkaExperienceEventViewModel;
  step1: WabunkaReservationFormStep1;
  step2: WabunkaReservationFormStep2;
  reservation: WabunkaReservationBase;
}
export interface WabunkaReservationWithGuestTypeForm {
  experienceFromCMS: EnExperienceViewModel;
  experienceEvent: WabunkaExperienceEventViewModel;
  step1: WabunkaReservationFormStep1;
  step2: WabunkaReservationFormStep2;
  reservation: WabunkaReservationWithGuestTypeBase;
}

export const createDefaultWabunkaReservationFormStep1 = (): WabunkaReservationForm['step1'] => ({
  numOfParticipants: 0,
  selectedDate: null,
  experienceEventId: '',
  totalPriceIncludingTax: 0,
  isValidated: false,
});
export const createDefaultWabunkaReservationFormStep2 = (): WabunkaReservationForm['step2'] => ({
  firstName: '',
  lastName: '',
  email: '',
  companyName: '',
  shouldShowSomeoneElseForm: false,
  forMyselfPhoneNumber: '',
  forMyselfNationality: '',
  participantFirstName: '',
  participantLastName: '',
  forSomeoneElsePhoneNumber: '',
  forSomeoneElseNationality: '',
  forSomeoneElseEmail: '',
  experienceEventOptions: [],
  experienceEventInterpreterOption: createDefaultWabunkaExperienceEventInterpreterOptionViewModel(),
  reservationQuestionAnswers: [],
  cardToken: '',
  cardLastFourDigits: '',
  paymentFailed: false,
  isValidated: false,
  code: { codeName: '' },
});

export const createDefaultWabunkaReservationForm = (): WabunkaReservationForm => ({
  experienceFromCMS: createDefaultEnExperienceViewModel(),
  experienceEvent: createDefaultWabunkaExperienceEventViewModel(),
  step1: createDefaultWabunkaReservationFormStep1(),
  step2: createDefaultWabunkaReservationFormStep2(),
  reservation: createDefaultWabunkaReservationBase(),
});

/**
 * APIレスポンスからBaseモデルを作成する
 *
 * @param {WabunkaReservation} res - APIレスポンス
 * @returns {WabunkaReservationBase} - Baseモデル
 */

export const buildWabunkaReservationBase = (res: WabunkaReservation | undefined): WabunkaReservationBase => {
  if (!res) return createDefaultWabunkaReservationBase();
  const reservedAt = res.reservedAt ? res.reservedAt.toISOString() : '';
  return {
    ...res,
    reservedAt: reservedAt,
    reservationExperienceEvent: buildWabunkaReservationExperienceEventViewModel(res.experienceEvent),
  };
};

const buildReservationExperienceEventOptionsInputRequest = (experienceEventOptions: WabunkaReservationExperienceEventOptionForm[]): WabunkaReservationExperienceEventOptionInputRequest[] =>
  experienceEventOptions.map((experienceEventOption) => ({
    experienceEventOptionId: experienceEventOption.experienceEventOption.id,
    count: experienceEventOption.count,
  }));

const buildQuestionAnswersInputRequest = (reservationQuestionAnswers: WabunkaReservationQuestionAnswerViewModel[]): WabunkaReservationQuestionAnswerInputRequest[] =>
  reservationQuestionAnswers.map((reservationQuestionAnswer) => ({
    questionId: reservationQuestionAnswer.id,
    answer: reservationQuestionAnswer.answer,
  }));

/**
 * 予約フォームのオプション選択を、計測（Meta Conversion API 等）で使う予約オプション
 * WabunkaReservedExperienceEventOption[] へ変換する。
 *
 * 3DS フローの done-reservation では予約レスポンスを直接持たないため、復元済みの step2 から組み立てる。
 * experienceEventOption は WabunkaExperienceEventOptionViewModel | WabunkaExperienceOptionViewModel の
 * ユニオンのため、共通フィールド（id / name / price）のみ参照する（Meta contents は id と price のみ使用）。
 */
export const buildReservedExperienceEventOptionsFromForm = (experienceEventOptions: WabunkaReservationExperienceEventOptionForm[]): WabunkaReservedExperienceEventOption[] =>
  experienceEventOptions.map(({ experienceEventOption, count }) =>
    WabunkaReservedExperienceEventOption.create({
      id: experienceEventOption.id,
      name: experienceEventOption.name,
      price: experienceEventOption.price,
      // taxRate は WabunkaExperienceEventOptionViewModel のみが持つ（ユニオンの片方）。
      // 計測の税込算出（calcPriceIncludingTax）に使うため、持っていれば設定する。
      taxRate: 'taxRate' in experienceEventOption ? experienceEventOption.taxRate : 0,
      count,
    }),
  );

/**
 * 予約フォームの通訳オプション選択を予約通訳オプション WabunkaReservedExperienceEventInterpreterOption へ変換する。
 * 未選択（id が空）の場合は undefined を返す。
 */
export const buildReservedExperienceEventInterpreterOptionFromForm = (
  interpreterOption: WabunkaExperienceEventInterpreterOptionViewModel,
): WabunkaReservedExperienceEventInterpreterOption | undefined =>
  interpreterOption.id !== ''
    ? WabunkaReservedExperienceEventInterpreterOption.create({
        id: interpreterOption.id,
        name: interpreterOption.name,
        price: interpreterOption.price,
        taxRate: interpreterOption.taxRate,
      })
    : undefined;

const buildPaymentMethodInputRequest = (paymentMethod: PaymentMethod): WabunkaPaymentMethod => {
  switch (paymentMethod) {
    case 'fincode':
      return WabunkaPaymentMethod.FINCODE;
    case 'payjp':
      return WabunkaPaymentMethod.PAYJP;
    default:
      return WabunkaPaymentMethod.METHOD_UNKNOWN;
  }
};

/**
 * Build experience event fees from guest counts for TimeSlotID cases
 */
const buildExperienceEventFeesInputRequest = (form: WabunkaReservationForm) => {
  const hasTimeSlotId = form.experienceEvent.timeSlotId !== '';

  // Only populate experienceEventFees for TimeSlotID cases with guest counts
  if (!hasTimeSlotId || !form.step1.guestCounts || !form.experienceEvent.experienceEventFees) {
    return [];
  }

  const experienceEventFees = [];

  // Map guest counts to experience event fees
  for (const [guestTypeId, count] of Object.entries(form.step1.guestCounts)) {
    if (count > 0) {
      // Find the corresponding experience fee for this guest type
      const experienceEventFee = form.experienceEvent.experienceEventFees.find((fee) => fee.guestType.id === guestTypeId);

      if (experienceEventFee) {
        experienceEventFees.push({
          experienceFeeId: experienceEventFee.experienceFeeId,
          orderCount: count,
        });
      }
    }
  }

  return experienceEventFees;
};

/**
 * step2 の入力値から予約ゲスト情報を組み立てる共通関数
 * shouldShowSomeoneElseForm の値に応じて For myself / For someone else のフィールドを振り分ける
 */
export const buildReservationGuestFromStep2 = (step2: WabunkaReservationFormStep2): WabunkaReservationGuest => {
  const base = { firstName: step2.firstName, lastName: step2.lastName, email: step2.email, companyName: step2.companyName };
  if (step2.shouldShowSomeoneElseForm) {
    return {
      ...base,
      participantFirstName: step2.participantFirstName,
      participantLastName: step2.participantLastName,
      participantPhoneNumber: step2.forSomeoneElsePhoneNumber,
      participantNationality: step2.forSomeoneElseNationality,
      participantEmail: step2.forSomeoneElseEmail,
    };
  }
  return {
    ...base,
    participantFirstName: step2.firstName,
    participantLastName: step2.lastName,
    participantPhoneNumber: step2.forMyselfPhoneNumber,
    participantNationality: step2.forMyselfNationality,
    participantEmail: step2.email,
  };
};

/**
 * form を引数に取り、リクエストボディを組み立てる
 * @param {WabunkaReservationForm} form - 予約作成フォーム
 * @returns {WabunkaReservationInputRequest} - 予約作成APIのリクエストボディ
 */
export const buildReservationInputRequest = (form: WabunkaReservationForm, paymentMethod: PaymentMethod): WabunkaReservationInputRequest => {
  const body: WabunkaReservationInputRequest = {
    experienceEventId: form.step1.experienceEventId,
    guestsNumber: form.step1.numOfParticipants,
    paymentMethod: buildPaymentMethodInputRequest(paymentMethod),
    guest: buildReservationGuestFromStep2(form.step2),
    experienceEventFees: buildExperienceEventFeesInputRequest(form),
    cardToken: form.step2.cardToken,
    experienceEventOptions: buildReservationExperienceEventOptionsInputRequest(form.step2.experienceEventOptions),
    experienceEventInterpreterOptionId: form.step2.experienceEventInterpreterOption.id,
    questionAnswers: buildQuestionAnswersInputRequest(form.step2.reservationQuestionAnswers),
    codeName: (form.step2.code.codeName !== '' && form.step2.code.codeName) || undefined,
  };

  return body;
};

export const createDefaultWabunkaReservationCart = (version: number): WabunkaReservationCart => ({
  version,
  items: [],
});

export const buildWabunkaReservationCartItem = (form: WabunkaReservationForm): WabunkaReservationCartItem => {
  // form.step2.reservationQuestionAnswersのanswerプロパティには個人情報が含まれる可能性があるので、それはローカルストレージには保存せず他の項目のみ保存する
  const reservationQuestions: WabunkaReservationQuestionViewModel[] = form.step2.reservationQuestionAnswers.map((reservationQuestionAnswer) => {
    return {
      id: reservationQuestionAnswer.id,
      content: reservationQuestionAnswer.content,
      placeHolder: reservationQuestionAnswer.placeHolder,
      isRequired: reservationQuestionAnswer.isRequired,
      sequenceId: reservationQuestionAnswer.sequenceId,
      questionType: reservationQuestionAnswer.questionType,
      answerOptions: reservationQuestionAnswer.answerOptions,
    };
  });

  return {
    formStep1: cloneDeep(form.step1),
    reservationQuestions: reservationQuestions,
    experienceEvent: cloneDeep(form.experienceEvent),
    cartAddedAt: dayjs(),
    cmsId: form.experienceFromCMS.id,
    pcMainImageUrl: form.experienceFromCMS.pcMainImage.url,
    title: form.experienceFromCMS.title,
  };
};

export const buildWabunkaReservationFormFromCartItem = (cartItem: WabunkaReservationCartItem): WabunkaReservationForm => {
  return {
    experienceFromCMS: {
      ...createDefaultEnExperienceViewModel(),
      id: cartItem.cmsId,
      title: cartItem.title,
      pcMainImage: {
        url: cartItem.pcMainImageUrl,
      },
    },
    experienceEvent: cloneDeep(cartItem.experienceEvent),
    step1: {
      ...cloneDeep(cartItem.formStep1),
      // 保存時に ISO 文字列へ直列化された日付を、JST の暦日として UTCモードで復元する。
      // dayjs(string) のローカルTZ解釈に任せると、カレンダー(UTCモード)と表現が食い違い
      // 非JST環境（北米等）で選択日のハイライトが1日ずれるため toJstCalendarDate で正規化する。
      selectedDate: cartItem.formStep1.selectedDate ? toJstCalendarDate(dayjs(cartItem.formStep1.selectedDate)) : null,
    },
    // step2はreservationQuestionAnswersの内answerを除いたプロパティのみcartItemに含まれる
    step2: {
      ...createDefaultWabunkaReservationFormStep2(),
      reservationQuestionAnswers: buildWabunkaReservationQuestionAnswerListViewModel(cartItem.reservationQuestions),
    },
    // reservationはcartItemに含まれないので初期値
    reservation: createDefaultWabunkaReservationBase(),
  };
};

/**
 * WabunkaReservationFormからWabunkaReservationWithGuestTypeFormに変換する
 */
export const convertToGuestTypeForm = (form: WabunkaReservationForm): WabunkaReservationWithGuestTypeForm => {
  // guestCountsからguestTypesを構築
  const guestTypes: WabunkaExperienceGuestTypePriceCalculationRequestInput[] = [];

  if (form.step1.guestCounts) {
    Object.entries(form.step1.guestCounts).forEach(([guestTypeId, count]) => {
      if (count > 0) {
        guestTypes.push({
          guestTypeId,
          count,
        });
      }
    });
  }

  const reservationWithGuestType: WabunkaReservationWithGuestTypeBase = {
    id: form.reservation.id,
    caseId: form.reservation.caseId,
    totalTax: form.reservation.totalTax,
    totalPriceIncludingTax: form.reservation.totalPriceIncludingTax,
    reservedAt: form.reservation.reservedAt,
    reservationExperienceEvent: form.reservation.reservationExperienceEvent,
    experienceEventOptions: form.reservation.experienceEventOptions,
    experienceEventInterpreterOption: form.reservation.experienceEventInterpreterOption,
    guestTypes,
  };

  return {
    experienceFromCMS: form.experienceFromCMS,
    experienceEvent: form.experienceEvent,
    step1: form.step1,
    step2: form.step2,
    reservation: reservationWithGuestType,
  };
};

/********************************************************************************************
 * 以下はマルチプライシング予約に使用する
 * TODO: マルチプライシング導入後無印(WabunkaReservationInputRequest)のものは削除する
 ********************************************************************************************/

/**
 * WabunkaReservationInputRequestV2 予約エンドポイントV2(マルチプライシング対応予約)で使うリクエスト構造体
 */
export interface WabunkaReservationInputRequestV2 {
  /** 体験イベントID */
  experienceEventId: string;
  /** 予約ゲスト */
  guest: WabunkaReservationGuest | undefined;
  /** 料金計算に必要な情報 */
  priceCalculationRequest: WabunkaExperiencePreReservationGuestTypeBasedPriceCalculationRequestInput | undefined;
  /** 決済カードトークン */
  cardToken: string;
  /** 決済方法 */
  paymentMethod?: WabunkaPaymentMethod | undefined;
  /** 予約時質問回答 */
  questionAnswers: WabunkaReservationQuestionAnswerInputRequest[];
}

/**
 * V2用のリクエストビルダー関数
 * form を引数に取り、V2エンドポイント用のリクエストボディを組み立てる
 * @param {WabunkaReservationForm} form - 予約作成フォーム
 * @param {PaymentMethod} paymentMethod - 決済方法
 * @returns {WabunkaReservationInputRequestV2} - 予約作成V2 APIのリクエストボディ
 */
export const buildReservationInputRequestV2 = (form: WabunkaReservationForm, paymentMethod: PaymentMethod): WabunkaReservationInputRequestV2 => {
  // ゲスト情報の構築
  const guest: WabunkaReservationGuest = buildReservationGuestFromStep2(form.step2);

  // 料金計算リクエストの構築
  let priceCalculationRequest: WabunkaExperiencePreReservationGuestTypeBasedPriceCalculationRequestInput | undefined;

  if (form.experienceEvent.timeSlotId && form.step1.guestCounts) {
    // ゲストタイプ別の予約の場合
    const guestTypes = Object.entries(form.step1.guestCounts)
      .filter(([, count]) => count > 0)
      .map(([guestTypeId, count]) => ({
        guestTypeId,
        count,
      }));

    priceCalculationRequest = {
      calculationInput: {
        guestTypes,
        guestTypePeopleCountSets: [], // FIXME: ゲストタイプ毎ゲスト人数の実装はこれから
        experienceEventOptions: form.step2.experienceEventOptions.map((option) => ({
          experienceEventOptionId: option.experienceEventOption.id,
          count: option.count,
        })),
        experienceEventInterpreterOptionId: form.step2.experienceEventInterpreterOption.id,
        experienceEventOptionReservedCountSets: [], // FIXME: experienceEventOptionReservedCountSetsの実装
      },
      codeName: form.step2.code.codeName !== '' ? form.step2.code.codeName : undefined,
    };
  }

  // 予約時質問回答の構築
  const questionAnswers: WabunkaReservationQuestionAnswerInputRequest[] = form.step2.reservationQuestionAnswers.map((answer) => ({
    questionId: answer.id,
    answer: answer.answer,
  }));

  // V2リクエストボディの構築
  const body: WabunkaReservationInputRequestV2 = {
    experienceEventId: form.step1.experienceEventId,
    guest,
    priceCalculationRequest,
    cardToken: form.step2.cardToken,
    paymentMethod: buildPaymentMethodInputRequest(paymentMethod),
    questionAnswers,
  };

  return body;
};
